import { IEventLoggingComponentProps, withEventLogging } from '@AppModels/Logging';
import { StyleDeclarationValue, css } from 'aphrodite';
import { Observer, Provider, inject, observer } from 'mobx-react';
import * as React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { IEditEmailTemplateContent, IImpersonationContextComponentProps, ILocationState } from '../../../../models';
import { IAdminUserSessionComponentProps } from '../../../../models/AdminModels';
import {
	FullScreenModalViewModelKey,
	IFullscreenModalComponentProps,
	UserSessionViewModelKey,
} from '../../../../models/AppState';
import {
	AccountOperationsViewModel,
	CreateCampaignApprovalViewModel,
	ImpersonationContextViewModel,
} from '../../../../viewmodels/AdminViewModels';
import { PrivateRoute } from '../../../../web/components/PrivateRoute';
import { BulkEmailApprovalConfigureSchedule } from '../BulkEmailApprovalConfigureSchedule';
import { BulkEmailApprovalConfirmation } from '../BulkEmailApprovalConfirmation';
import { BulkEmailApprovalMessageEditor } from '../BulkEmailApprovalMessageEditor';
import { BulkEmailApprovalRecipientsSelect } from '../BulkEmailApprovalRecipientsSelect';
import { BulkEmailApprovalSendNote } from '../BulkEmailApprovalSendNote';
import { BulkEmailApprovalTagSelect } from '../BulkEmailApprovalTagSelect';
import { styleSheet } from './styles';

interface IProps
	extends IEventLoggingComponentProps,
		RouteComponentProps<any>,
		IFullscreenModalComponentProps,
		IAdminUserSessionComponentProps {
	className?: string;
	styles?: StyleDeclarationValue[];
}

interface IState {
	accountOperations?: AccountOperationsViewModel;
}

class _CreateBulkEmailApproval extends React.Component<IProps, IState> {
	private mApproval: CreateCampaignApprovalViewModel;
	private mImpersonationProviderProps: IImpersonationContextComponentProps = {
		impersonationContext: new ImpersonationContextViewModel(),
	};

	constructor(props: IProps) {
		super(props);

		this.mApproval = new CreateCampaignApprovalViewModel(props.userSession);
		this.state = {};
	}

	public componentDidMount() {
		const { logPageView, match, history } = this.props;

		logPageView('CreateBulkEmailApproval');

		const locationState: ILocationState<AccountOperationsViewModel, any> = history?.location?.state;
		const checkLocation = () => {
			let replacePath: string = null;
			if (match.isExact && match.path?.toLocaleLowerCase() === '/email/bulk-email-approval') {
				replacePath = `${match.path}/tag-select`;
			}
			if (replacePath) {
				history.replace(replacePath);
			}
		};
		if (locationState) {
			const account = locationState.viewModel?.account;

			this.mApproval.account = account;
			this.mImpersonationProviderProps = {
				impersonationContext: new ImpersonationContextViewModel(account ? { account } : null),
			};
			this.mApproval.impersonate(
				this.mImpersonationProviderProps.impersonationContext.isValid
					? this.mImpersonationProviderProps.impersonationContext
					: null
			);
			this.setState(
				{
					accountOperations: locationState.viewModel,
				},
				checkLocation
			);
		} else {
			checkLocation();
		}
	}

	public render() {
		const { className, styles, match, location, userSession } = this.props;
		const { accountOperations } = this.state;
		if (!accountOperations || !this.mImpersonationProviderProps?.impersonationContext?.isValid) {
			return null;
		}

		return (
			<Provider {...this.mImpersonationProviderProps}>
				<div
					className={`${css(styleSheet.container, ...(styles || []))} create-bulk-email-approval ${className || ''}`}
				>
					<PrivateRoute
						location={location}
						path={`${match.path}/tag-select`}
						render={this.onRenderTagSelect}
						userSession={userSession}
					/>
					<PrivateRoute
						location={location}
						path={`${match.path}/recipients`}
						render={this.onRenderRecipientsSelect}
						userSession={userSession}
					/>
					<PrivateRoute
						location={location}
						path={`${match.path}/schedule-send`}
						render={this.onRenderScheduleSend}
						userSession={userSession}
					/>
					<PrivateRoute
						location={location}
						path={`${match.path}/message`}
						render={this.onRenderMessageEditor}
						userSession={userSession}
					/>
					<PrivateRoute
						location={location}
						path={`${match.path}/send-note`}
						render={this.onRenderSendNote}
						userSession={userSession}
					/>
					<PrivateRoute
						location={location}
						path={`${match.path}/confirmation`}
						render={this.onRenderConfirmation}
						userSession={userSession}
					/>
				</div>
			</Provider>
		);
	}

	private onRenderTagSelect = (props: RouteComponentProps<any>) => {
		const { accountOperations } = this.state;
		return (
			<BulkEmailApprovalTagSelect
				{...props}
				account={accountOperations?.account}
				approval={this.mApproval}
				onTagSelected={this.onTagSelected}
			/>
		);
	};

	private onTagSelected = (tag: string) => {
		this.mApproval.setInitialTag(tag);
		this.onNavigateTo('/recipients')();
	};

	private onRenderRecipientsSelect = (props: RouteComponentProps<any>) => {
		const { accountOperations } = this.state;
		return (
			<div className={css(styleSheet.recipientsContainer)}>
				<BulkEmailApprovalRecipientsSelect
					{...props}
					account={accountOperations?.account}
					approval={this.mApproval}
					onRequestNext={this.advanceToTemplateSelect}
				/>
			</div>
		);
	};

	private onRenderScheduleSend = (props: RouteComponentProps<any>) => {
		return (
			<Observer>
				{() => {
					return (
						<BulkEmailApprovalConfigureSchedule
							{...props}
							onRenderMessage={this.onRenderScheduleSendMessage}
							onSelected={this.onSendDateSelected}
							onSendDateUpdated={this.onSendDateUpdated}
							sendDate={this.mApproval.sendDate}
							styles={[styleSheet.configureSchedule]}
						/>
					);
				}}
			</Observer>
		);
	};

	private onRenderMessageEditor = (props: RouteComponentProps<any>) => {
		return <BulkEmailApprovalMessageEditor {...props} approval={this.mApproval} onFinish={this.onTemplateSelected} />;
	};

	private onRenderSendNote = (props: RouteComponentProps<any>) => {
		return <BulkEmailApprovalSendNote {...props} approval={this.mApproval} onNotify={this.onClientNotified} />;
	};

	private onClientNotified = () => {
		this.onNavigateTo('/confirmation')();
	};

	private onRenderScheduleSendMessage = (datePassed: boolean) => {
		return datePassed
			? 'Please pick a new date to send this campaign. The date suggested by your Success Manager has passed (in orange)'
			: 'Your success manager has recommended the following time to send the campaign:';
	};

	private advanceToTemplateSelect = () => {
		// add user to impersonation context

		this.mImpersonationProviderProps.impersonationContext.update({
			account: this.mImpersonationProviderProps.impersonationContext.account,

			user:
				this.mImpersonationProviderProps.impersonationContext.isValid && this.mApproval.selectedUsers.length === 1
					? this.mApproval.selectedUsers.getByIndex(0)?.toJs()
					: null,
		});
		this.onNavigateTo('/message')();
	};

	private onSendDateUpdated = (sendDate: Date) => {
		this.mApproval.setSendDate(sendDate);
	};

	private onSendDateSelected = () => {
		this.onNavigateTo('/send-note')();
	};

	private onTemplateSelected = (content?: IEditEmailTemplateContent) => {
		this.mApproval.emailContent = content;
		this.onNavigateTo('/schedule-send')();
	};

	private onNavigateTo = (path: string, locationState?: ILocationState<any, any>) => () => {
		const { history, match } = this.props;
		history.push(`${match.path}${path}`, locationState);
	};

	private onRenderConfirmation = (props: RouteComponentProps<any>) => {
		return (
			<BulkEmailApprovalConfirmation
				{...props}
				approval={this.mApproval}
				onRequestClose={this.onConfirmationComplete}
			/>
		);
	};

	private onConfirmationComplete = () => {
		const { fullscreenModal } = this.props;
		// Update the group email VM here depending on how the info is served from the API.

		fullscreenModal.history.goBackToLocationBeforeTrackedHistory(true);
	};
}

const CreateBulkEmailApprovalAsObserver = observer(_CreateBulkEmailApproval);
const CreateBulkEmailApprovalWithContext = inject(
	UserSessionViewModelKey,
	FullScreenModalViewModelKey
)(CreateBulkEmailApprovalAsObserver);
export const CreateBulkEmailApproval = withEventLogging(CreateBulkEmailApprovalWithContext, 'CreateBulkEmailApproval');
